<template>
  <div class="page-container">
    <!-- 头部容器 -->
    <div class="header-container">
      <div class="left-container">
        <!-- 结算口径切换：仅在电站开启现货收益结算（deviceFlag bit5）时出现。
             spotEnabled 为 undefined 表示尚未确定，此时不渲染，避免先按抄表渲染再跳成现货 -->
        <el-radio-group v-model="settleMode" class="settle-mode-group" v-if="spotEnabled === true">
          <el-radio-button label="meter">抄表结算</el-radio-button>
          <el-radio-button label="spot">现货结算</el-radio-button>
        </el-radio-group>

        <!-- 抄表口径：原有的按日/按月/按年 -->
        <el-radio-group v-model="pageCtrlData.timeUnit" v-show="!isSpot">
          <el-radio-button label="day">{{ $str.getKey('收益管理/按日') }}</el-radio-button>
          <el-radio-button label="month">{{ $str.getKey('收益管理/按月') }}</el-radio-button>
          <el-radio-button label="year">{{ $str.getKey('收益管理/按年') }}</el-radio-button>
        </el-radio-group>

        <!-- 现货口径：独立的颗粒度切换 -->
        <el-radio-group v-model="granularity" v-if="isSpot">
          <el-radio-button v-for="g in SPOT_GRANULARITIES" :key="g.value" :label="g.value">
            {{ g.label }}
          </el-radio-button>
        </el-radio-group>

        <div class="time-picker-container">
          <el-date-picker v-model="pageCtrlData.timeRange" @change="timeRangeChange" :disabled-date="disabledDate" :range-separator="$str.getKey('收益管理/至')"
                          :clearable="false" :type="isSpot ? 'daterange' : pageCtrlData.rangeType" value-format="YYYY-MM-DD"></el-date-picker>
        </div>
        <div class="select-meter-container">
          <div class="text-label">{{ $str.getKey('收益管理/选择电表') }}</div>
          <el-select v-model="pageCtrlData.selectedMeter" :placeholder="$str.getKey('收益管理/请选择电表')" @change="selectedMeterChange">
            <el-option :label="$str.getKey('收益管理/全部')" value="all" v-if="pageCtrlData.meterList.length > 1"></el-option>
            <el-option v-for="item in pageCtrlData.meterList" :label="item.meterName" :value="item.uuid" :key="item.uuid"></el-option>
          </el-select>
        </div>

      </div>

      <div class="right-container" v-if="false">
        <div class="switch-container">
          <el-switch v-model="pageCtrlData.isSubscribed" style="--el-switch-on-color: #13ce66;" />
          <div class="text-label">{{ $str.getKey('收益管理/订阅') }}</div>
        </div>
        <el-button jd-type="primary">{{ $str.getKey('收益管理/周报') }}</el-button>
        <el-button jd-type="primary">{{ $str.getKey('收益管理/月报') }}</el-button>
      </div>

    </div>
    <!-- 主体容器：上下左右四个块 -->
    <div class="main-container">
      <div class="overview-grid" :class="{ 'is-spot': isSpot }">
        <!-- 左上：收益统计图表 -->
        <div class="overview-block block-top-left">
          <SpotIncomeChartPanel v-if="isSpot" :rows="spotRows" :granularity="granularity" :loading="spotLoading" />
          <IncomeChartPanel v-else :chartData="chartPanelData"></IncomeChartPanel>
        </div>
        <!-- 右上：充放电总览（WS 实时累计口径，仅抄表结算展示）
             现货口径的电量来自结算接口，与 WS 累计不同源，同屏并列会让用户以为两个数应该对得上 -->
        <div class="overview-block block-top-right" v-if="!isSpot">
          <FirstRightPanel></FirstRightPanel>
        </div>
        <!-- 左下：充放电统计图表 -->
        <div class="overview-block block-bottom-left">
          <SpotKwhChartPanel v-if="isSpot" :rows="spotRows" :granularity="granularity" :loading="spotLoading" />
          <KwhChartPanel v-else :chartData="chartPanelData"></KwhChartPanel>
        </div>
        <!-- 右下：所选日期范围内 / 现货数据完整性（现货口径不渲染，避免留白框） -->
        <div class="overview-block block-bottom-right" v-if="!isSpot">
          <!-- <SpotQualityPanel  :summary="spotSummary" /> -->
          <SecondRightPanel :panelData="abnormalPanelData"></SecondRightPanel>
        </div>
        
      </div>

      <!-- 底部收益统计表格：宽度与左侧图表一致 -->
      <div class="bottom-table-wrapper">
        <div class="bottom-table-block">
          <SpotDetailTablePanel
            v-if="isSpot"
            :rows="spotRows"
            :granularity="granularity"
            :loading="spotLoading"
            :summary="spotSummary"
            :meterMap="meterMap"
          >
            <template #actions>
              <el-button type="primary" plain size="small" :disabled="!spotRows.length" @click="handleExportSpot">
                导出明细
              </el-button>
            </template>
          </SpotDetailTablePanel>
          <DetailTablePanel v-else :tableData="tablePanelData" :meterUuid="pageCtrlData.selectedMeter"></DetailTablePanel>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUpdated, nextTick } from "vue"
import dayjs from 'dayjs';
import { isValidJSON } from '@/utils/index'
import { queryStationMeterList, queryIncomeInfoByTimeRange, queryAbnormalInfo } from '../components/over-view/api/index.js';
import useAppStore from '@/store/modules/app'
import IncomeChartPanel from "../components/over-view/incomeChartPanel/index.vue"
import KwhChartPanel from "../components/over-view/kwhChartPanel/index.vue"
import FirstRightPanel from "../components/over-view/firstRightPanel/index.vue"
import SecondRightPanel from "../components/over-view/secondRightPanel/index.vue"
import DetailTablePanel from "../components/over-view/detailTablePanel/index.vue"
import SpotIncomeChartPanel from "../components/over-view/spot/SpotIncomeChartPanel/index.vue"
import SpotKwhChartPanel from "../components/over-view/spot/SpotKwhChartPanel/index.vue"
import SpotQualityPanel from "../components/over-view/spot/SpotQualityPanel/index.vue"
import SpotDetailTablePanel from "../components/over-view/spot/SpotDetailTablePanel/index.vue"
import { useSpotSettlement, SPOT_GRANULARITIES } from "../composables/useSpotSettlement.js"
import { exportSpotDetail } from "../composables/exportSpotDetail.js"
import { ElMessage } from 'element-plus'

const $str = window.$str;

/** 获取stationId，并监听其变化-更新数据 */
const appStore = useAppStore();
const stationId = computed(() => appStore.stationId);
const incomeModel = computed(() => appStore.incomeModel);

const loading = ref(false);

const pageCtrlData = reactive({
  timeUnit: 'day',
  // 初始化为字符串格式，与 value-format="YYYY-MM-DD" 保持一致，避免Date对象时区问题
  timeRange: [dayjs().subtract(1, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
  rangeType: 'daterange',
  meterList: [],
  selectedMeter: 'all',
  isSubscribed: true, // 是否订阅
})

const chartPanelData = reactive({
  income: [],
  kwh: [],
  incomeModel: incomeModel.value || 1, // 初始化时设置默认值
})

/** ====== [现货收益结算] ===================================================================== */
const spot = useSpotSettlement({ defaultGranularity: '15min' })
const spotEnabled = spot.spotEnabled
const granularity = spot.granularity
const isSpot = spot.isSpot
const spotRows = spot.rows
const spotLoading = spot.loading
const spotSummary = spot.summary
const maxSpanDays = spot.maxSpanDays

/** 导出当前区间的全量现货明细 */
function handleExportSpot() {
  const [start, end] = pageCtrlData.timeRange || []
  const ok = exportSpotDetail({
    rows: spotRows.value,
    granularity: granularity.value,
    meterMap: meterMap.value,
    stationName: appStore.stationName,
    startDate: start,
    endDate: end,
  })
  if (!ok) ElMessage.warning('当前没有可导出的明细数据')
}

// 走 setSettleMode 而不是直接改 ref，让 composable 记录「用户已手动选择」
const settleMode = computed({
  get: () => spot.settleMode.value,
  set: (v) => spot.setSettleMode(v),
})

/** uuid -> meterName，供现货明细表把电表列渲染成名称 */
const meterMap = computed(() => {
  const map = {}
  pageCtrlData.meterList.forEach((it) => {
    if (it?.uuid) map[it.uuid] = it.meterName
  })
  return map
})

/** 现货取数：日期区间取自同一个 timeRange，电表沿用同一个下拉 */
async function refreshSpotData() {
  const [start, end] = pageCtrlData.timeRange || []
  if (!start || !end) return
  const startDate = String(start).substring(0, 10)
  const endDate = String(end).substring(0, 10)
  const span = spot.validateSpan(startDate, endDate)
  if (!span.ok) {
    ElMessage.warning(span.message)
    return
  }
  await spot.fetchSpotStat({
    startDate,
    endDate,
    uuid: pageCtrlData.selectedMeter,
  })
}

/**
 * 把日期区间收敛到当前颗粒度的上限内。
 * 从「按月/按年」切进现货时 timeRange 往往是一年跨度，15min 颗粒度直接查会被拦下，
 * 这里主动裁到合法区间，而不是甩个警告让用户自己改。
 */
function clampTimeRangeForSpot() {
  const limit = maxSpanDays.value
  if (limit == null) return
  const [start, end] = pageCtrlData.timeRange || []
  if (!start || !end) return
  const startDate = String(start).substring(0, 10)
  const endDate = String(end).substring(0, 10)
  const spanDays = dayjs(endDate).diff(dayjs(startDate), 'day') + 1
  if (spanDays <= limit) return
  const newStart = dayjs(endDate).subtract(limit - 1, 'day').format('YYYY-MM-DD')
  pageCtrlData.timeRange = [newStart, endDate]
}

/**
 * 切换到现货口径时把日期区间重置为昨天，方便用户快速查最新数据。
 * 切回抄表时恢复为上个月（与初始值一致）。
 */
function adjustDateForMode(toSpot) {
  if (toSpot) {
    // 进入现货：昨天整天
    const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD')
    pageCtrlData.timeRange = [yesterday, yesterday]
  } else {
    // 回到抄表：默认上个月
    const startDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD')
    const endDate = dayjs().format('YYYY-MM-DD')
    pageCtrlData.timeRange = [startDate, endDate]
  }
}

// 进入现货模式、切颗粒度时先重置/收敛区间再取数
watch([isSpot, granularity], ([nowSpot, nowGran], [prevSpot, prevGran]) => {
  if (!nowSpot) return
  const justEntered = nowSpot && !prevSpot
  if (justEntered) {
    // 重置日期后若区间确实变了，timeRange 的 watch 会接力触发取数；
    // 但区间本来就等于「昨天」时 watch 不会触发，必须在这里补一次请求，
    // 否则 rows 为空、右侧完整性面板全是 "--"
    const beforeEnter = (pageCtrlData.timeRange || []).join('~')
    adjustDateForMode(true)
    if ((pageCtrlData.timeRange || []).join('~') === beforeEnter) {
      refreshSpotData()
    }
    return
  }
  // 颗粒度切换时先收敛区间
  const before = (pageCtrlData.timeRange || []).join('~')
  clampTimeRangeForSpot()
  // 区间被裁过的话，timeRange 的 watch 会接着触发 refreshPageData，这里不必重复请求
  if ((pageCtrlData.timeRange || []).join('~') === before) {
    refreshSpotData()
  }
})

// 从现货切回抄表时恢复日期区间到默认值
watch(() => settleMode.value, (newMode, oldMode) => {
  if (oldMode === 'spot' && newMode === 'meter') {
    adjustDateForMode(false)
  }
})

const exportTimeRange = computed(() => {
  const tr = [dayjs().format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')];
  
  // 处理开始日期，确保使用本地时间的年月日
  const getDateString = (dateValue) => {
    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear();
      const month = dateValue.getMonth() + 1;
      const day = dateValue.getDate();
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    } else if (typeof dateValue === 'string') {
      return dateValue.substring(0, 10);
    } else {
      return dayjs(dateValue).format('YYYY-MM-DD');
    }
  };
  
  if (pageCtrlData.timeUnit === 'day') {
    tr[0] = getDateString(pageCtrlData.timeRange[0]);
    tr[1] = getDateString(pageCtrlData.timeRange[1]);
    return tr;
  }
  if (pageCtrlData.timeUnit === 'month') {
    tr[0] = getDateString(pageCtrlData.timeRange[0]);
    const endDate = getDateString(pageCtrlData.timeRange[1]);
    if (dayjs(endDate).isSame(dayjs(), 'month')) {
      tr[1] = dayjs().format('YYYY-MM-DD');
    } else {
      tr[1] = dayjs(endDate).endOf('month').format('YYYY-MM-DD');
    }
    return tr;
  }
  if (pageCtrlData.timeUnit === 'year') {
    tr[0] = getDateString(pageCtrlData.timeRange[0]);
    const endDate = getDateString(pageCtrlData.timeRange[1]);
    if (dayjs(endDate).isSame(dayjs(), 'year')) {
      tr[1] = dayjs().format('YYYY-MM-DD');
    } else {
      tr[1] = dayjs(endDate).endOf('year').format('YYYY-MM-DD');
    }
    return tr;
  }
})

const tablePanelData = reactive({
  list: [],
  stationId: stationId,
  exportTimeRange: exportTimeRange,
  incomeModel: incomeModel,
})

const abnormalPanelData = reactive({
  timeRange: exportTimeRange,
})

// 监听 stationId 变化（必须在 pageCtrlData 和所有依赖的函数定义之后）
watch(() => appStore.stationId, (newStationId) => {
// 只有当 stationId 有值时才初始化页面
  if (newStationId) {
    initPage();
  }
}, { immediate: true });

// 监听 incomeModel 变化，更新 chartPanelData.incomeModel
watch(() => incomeModel.value, (newModel) => {
  chartPanelData.incomeModel = newModel || 1;
}, { immediate: true });

/** ====== [业务：功能方法] =================================================================== */

function initPage() {
  getMeterList();
  refreshPageData();
}

watch(() => pageCtrlData.timeUnit, nv => {
  if (nv === 'day') {
    pageCtrlData.rangeType = 'daterange';
    // 使用字符串格式，与 value-format="YYYY-MM-DD" 保持一致，避免Date对象时区问题
    const startDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
    const endDate = dayjs().format('YYYY-MM-DD');
    pageCtrlData.timeRange = [startDate, endDate];
    // pageCtrlData.timeRange = [dayjs('2022-08-25').subtract(1, 'month').format('YYYY-MM-DD'), dayjs('2022-07-31').format('YYYY-MM-DD')]; // dev---mock---异常数据-浙江义乌文渊
  }
  if (nv === 'month') {
    pageCtrlData.rangeType = 'monthrange';
    const startDate = dayjs().subtract(1, 'year').startOf('month').format('YYYY-MM-DD');
    const endDate = dayjs().format('YYYY-MM-DD');
    pageCtrlData.timeRange = [startDate, endDate];
  }
  if (nv === 'year') {
    const startDate = dayjs().subtract(10, 'year').startOf('year').format('YYYY-MM-DD');
    const endDate = dayjs().format('YYYY-MM-DD');
    pageCtrlData.timeRange = [startDate, endDate];
    pageCtrlData.rangeType = 'monthrange';
  }
}, {immediate: true})

/** 时间范围发生变化 */
function timeRangeChange() {
  // refreshPageData();

}


watch(() => pageCtrlData.timeRange, nv => {
  // ------ 当前element版本，不支持按年选择，可以通过时间范围强行设置按整年范围选择 ------
  // if (pageCtrlData.timeUnit === 'year') {
  //   pageCtrlData.timeRange[0] = dayjs(pageCtrlData.timeRange[0]).startOf('year');
  //   if (dayjs(pageCtrlData.timeRange[1]).isSame(dayjs(), 'year')) {
  //     pageCtrlData.timeRange[1] = dayjs();
  //   } else {
  //     pageCtrlData.timeRange[1] = dayjs(pageCtrlData.timeRange[1]).endOf('year');
  //   }
  // }

  // 避免在初始化时重复调用，确保有有效的时间范围
  if (nv && Array.isArray(nv) && nv.length === 2 && nv[0] && nv[1]) {
    refreshPageData();
  }
})



function selectedMeterChange(meter) {
  refreshPageData();
}


function refreshPageData() {
  // 现货口径走独立的数据源，不触碰原有的 getIncomeInfo
  if (isSpot.value) {
    refreshSpotData();
    return;
  }
  getIncomeInfo();
}



/** ====== [辅助：功能方法] =================================================================== */

/** 设置日期的禁选范围 */
function disabledDate(date) {
  // const latestHalfYear = dayjs().subtract(6, 'month');
  // return dayjs(date) > dayjs() || dayjs(date) < latestHalfYear;
  return dayjs(date) > dayjs();
}



function getMeterList() {
  // 如果没有 stationId，不执行请求
  if (!stationId.value) {
    console.warn('stationId is not available, skip getMeterList');
    pageCtrlData.meterList = [];
    pageCtrlData.selectedMeter = '';
    return;
  }
  
  pageCtrlData.selectedMeter = ''; // 切换电站-获取电表列表前-先重置选中的电表项-避免影响收益数据请求时参数携带上个电站的电表
  const params = {
    stationId: stationId.value
  }
  // loading.value = true;
  queryStationMeterList(params).then(res => {
    if (Array.isArray(res?.rows)) {
      // const targets = res.rows.filter(it => it.meterName.includes('一次'));
      // console.log('dev---: 电表列表&一次表 = ',targets, res.rows)


      // 直接使用后端返回的电表名称：名称里的 - / _ 是名称本身的一部分
      // （如 "1-并网表"），按分隔符截断会把编号当成整个名称显示成 "1"、"2"
      pageCtrlData.meterList = res.rows.filter(mt => mt.meterType == 3);

      // // 针对【津西钢铁储能电站】开放具备【一次值特征】的电表的数据查看
      // if (stationId.value === 656) {
      //   const ycMeters = res.rows.filter(mt => mt.meterType == 4);
      //   pageCtrlData.meterList.push(...ycMeters);
      //   console.log('dev---: 电表列表-4 = ',targets, ycMeters)
      // }

      // 刷新：电表默认选择项
      if (pageCtrlData.meterList.length < 1) {
        pageCtrlData.selectedMeter = '';
      } else if (pageCtrlData.meterList.length === 1) {
        pageCtrlData.selectedMeter = pageCtrlData.meterList[0].uuid;
      } else {
        pageCtrlData.selectedMeter = 'all';
      }
    }
  }).catch(err => {
    pageCtrlData.meterList = [];
    pageCtrlData.selectedMeter = '';
  }).finally(() => {
    // loading.value = false;
  })
}

function getIncomeInfo() {
  // 如果没有 stationId，不执行请求
  if (!stationId.value) {
    console.warn('stationId is not available, skip getIncomeInfo');
    return;
  }
  
  // pageCtrlData.timeRange 现在是字符串格式 'YYYY-MM-DD'，直接使用
  // 如果还是Date对象（兼容处理），使用本地时间的年月日
  let startDateStr = pageCtrlData.timeRange[0];
  if (startDateStr instanceof Date) {
    // 如果是Date对象，使用本地时间的年月日，避免时区问题
    const year = startDateStr.getFullYear();
    const month = startDateStr.getMonth() + 1; // getMonth() 返回 0-11
    const day = startDateStr.getDate();
    startDateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  } else if (typeof startDateStr === 'string') {
    // 已经是字符串格式，直接使用
    startDateStr = startDateStr.substring(0, 10); // 确保只取日期部分
  } else {
    // dayjs对象或其他，转换为字符串
    startDateStr = dayjs(startDateStr).format('YYYY-MM-DD');
  }
  
  const startTime = `${startDateStr} 00:00:00`;
  const endTime = getEndTimeForApiParam(pageCtrlData.timeRange[1]);
  console.log("进入方法")
  console.log("原始timeRange[0]:", pageCtrlData.timeRange[0], "类型:", typeof pageCtrlData.timeRange[0], pageCtrlData.timeRange[0] instanceof Date)
  console.log("处理后的startDateStr:", startDateStr)
  console.log("startTime : "+startTime + " endTime : " + endTime)
  const meterUuid = pageCtrlData.selectedMeter === 'all' ? '' : pageCtrlData.selectedMeter;
  const params = {
    stationId: stationId.value,
    timeUint: pageCtrlData.timeUnit,
    startTime,
    endTime,
    meterUuid, // '06-V21-2',
  }
  loading.value = true;
  queryIncomeInfoByTimeRange(params).then(res => {
    const incomePointArr = [];
    const kwhPointArr = [];
    const tableList = [];
    if (Array.isArray(res.data)) {
      res.data.forEach(p => {
        const time = formateTime(p.date);

        const incomePoint = {
          time,
          value: p.sum_income
        }
        incomePointArr.push(incomePoint);

        const kwhPoint = {
          time,
          inSum: p.sum_e_positive,
          inSharp: p.sum_e_positive_sharp ?? '--',
          inPeak: p.sum_e_positive_peak ?? '--',
          inFlat: p.sum_e_positive_flat ?? '--',
          inValley: p.sum_e_positive_valley ?? '--',
          inDeepValley: p.sum_e_positive_deep_valley ?? '--',
          outSum: p.sum_e_reverse,
          outSharp: p.sum_e_reverse_sharp ?? '--',
          outPeak: p.sum_e_reverse_peak ?? '--',
          outFlat: p.sum_e_reverse_flat ?? '--',
          outValley: p.sum_e_reverse_valley ?? '--',
          outDeepValley: p.sum_e_reverse_deep_valley ?? '--',
        }
        kwhPointArr.push(kwhPoint);

        const tableItem = {
          time,
          inSum: p.sum_e_positive,
          inSharp: p.sum_e_positive_sharp ?? '--',
          inPeak: p.sum_e_positive_peak ?? '--',
          inFlat: p.sum_e_positive_flat ?? '--',
          inValley: p.sum_e_positive_valley ?? '--',
          inDeepValley: p.sum_e_positive_deep_valley ?? '--',
          outSum: p.sum_e_reverse,
          outSharp: p.sum_e_reverse_sharp ?? '--',
          outPeak: p.sum_e_reverse_peak ?? '--',
          outFlat: p.sum_e_reverse_flat ?? '--',
          outValley: p.sum_e_reverse_valley ?? '--',
          outDeepValley: p.sum_e_reverse_deep_valley ?? '--',
          incomeSum: p.sum_income ?? '--',
          priceInfo: formatPriceInfo(p.price),
        }
        tableList.push(tableItem);

      });
    }
    chartPanelData.income = incomePointArr;
    chartPanelData.kwh = kwhPointArr;
    chartPanelData.incomeModel = incomeModel.value || 1; // 更新 incomeModel
    tablePanelData.list = tableList;

    addAbnormalInfo(); // 检查哪些日期存在异常，并添加标注字段

  }).finally(() => {
    loading.value = false;
  })
}


/**
 * 收益明细列表数据-添加异常数据信息
 */
function addAbnormalInfo() {
  // 处理日期，确保使用本地时间的年月日
  const getDateString = (dateValue) => {
    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear();
      const month = dateValue.getMonth() + 1;
      const day = dateValue.getDate();
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    } else if (typeof dateValue === 'string') {
      return dateValue.substring(0, 10);
    } else {
      return dayjs(dateValue).format('YYYY-MM-DD');
    }
  };
  
  const startDateStr = getDateString(pageCtrlData.timeRange[0]);
  const endDateStr = getDateString(pageCtrlData.timeRange[1]);
  
  const params = {
    stationId: stationId.value,
    startTime: `${startDateStr} 00:00:00`,
    endTime: `${endDateStr} 23:59:59`,
  }
  queryAbnormalInfo(params).then(res => {
    if (Array.isArray(res?.data?.errorNum)) {
      const abnormalDayList = res.data.errorNum;
      abnormalDayList.forEach(d => {
        const dayInfo = tablePanelData.list.find(item => d.date.startsWith(item.time));
        if (dayInfo) {
          dayInfo.abnormalInfo = {
            type: 'error',
            tip: $str.getKey('收益管理/电表数据异常'),
            origin: d,
          }

        }
      })
    }
  })
}


function formateTime(time, unit = pageCtrlData.timeUnit) {
  // 判断time参数是否为有效的日期
  const tmp = dayjs(time);
  if (!tmp.isValid) {
    return '';
  }

  if (unit === 'day') {
    return tmp.format('YYYY-MM-DD')
  }

  if (unit === 'month') {
    return tmp.format('YYYY-MM')
  }

  if (unit === 'year') {
    return tmp.format('YYYY')
  }

}


/** 格式化：电价数据 */
function formatPriceInfo(priceJson) {
  const priceData = [];

  if (isValidJSON(priceJson)) {
    const p = JSON.parse(priceJson);
    priceData.push(
      {
        type: $str.getKey('收益管理/充电'),
        sharp: p.in_sharp_price ?? '--',
        peak: p.in_peak_price ?? '--',
        flat: p.in_flat_price ?? '--',
        valley: p.in_valley_price ?? '--',
        deepValley: p.in_deep_valley_price ?? '--',
      },
      {
        type: $str.getKey('收益管理/放电'),
        sharp: p.out_sharp_price ?? '--',
        peak: p.out_peak_price ?? '--',
        flat: p.out_flat_price ?? '--',
        valley: p.out_valley_price ?? '--',
        deepValley: p.out_deep_valley_price ?? '--',
      }
    )
  }

  return priceData
}


/**
 * 格式化API接口需要的结束时间
 * >>> 按月 / 按年
 * >>> 选中月份在当月的情况下，结束日期应该为当天
 */
function getEndTimeForApiParam(time) {
  // 处理日期，确保使用本地时间的年月日
  const getDateString = (dateValue) => {
    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear();
      const month = dateValue.getMonth() + 1;
      const day = dateValue.getDate();
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    } else if (typeof dateValue === 'string') {
      return dateValue.substring(0, 10);
    } else {
      return dayjs(dateValue).format('YYYY-MM-DD');
    }
  };
  
  const dateStr = getDateString(time);
  
  // 日
  if (pageCtrlData.timeUnit === 'day') {
    return `${dateStr} 23:59:59`;
  }
  // 月
  if (pageCtrlData.timeUnit === 'month') {
    if (dayjs(dateStr).isSame(dayjs(), 'month')) {
      return dayjs().format('YYYY-MM-DD 23:59:59');
    } else {
      return dayjs(dateStr).endOf('month').format('YYYY-MM-DD 23:59:59');
    }
  }
  // 年
  if (pageCtrlData.timeUnit === 'year') {
    if (dayjs(dateStr).isSame(dayjs(), 'year')) {
      return dayjs().format('YYYY-MM-DD 23:59:59');
    } else {
      return dayjs(dateStr).endOf('year').format('YYYY-MM-DD 23:59:59');
    }
  }
}

/** 修复下拉框文本垂直居中 */
function fixSelectTextAlignment() {
  nextTick(() => {
    const selectContainer = document.querySelector('.select-meter-container');
    if (!selectContainer) {
      return;
    }
    
    // 查找所有相关的输入框元素
    const inputWrappers = selectContainer.querySelectorAll('.el-input__wrapper');
    const inputInners = selectContainer.querySelectorAll('.el-input__inner');
    const selectedItems = selectContainer.querySelectorAll('.el-select__selected-item');
    const selectWrapper = selectContainer.querySelector('.el-select__wrapper');
    const inputElements = selectContainer.querySelectorAll('.el-input');
    
    // 设置 wrapper 样式 - 完全覆盖
    inputWrappers.forEach(wrapper => {
      wrapper.style.setProperty('display', 'flex', 'important');
      wrapper.style.setProperty('align-items', 'center', 'important');
      wrapper.style.setProperty('justify-content', 'flex-start', 'important');
      wrapper.style.setProperty('padding-top', '0', 'important');
      wrapper.style.setProperty('padding-bottom', '0', 'important');
      wrapper.style.setProperty('height', '32px', 'important');
      wrapper.style.setProperty('min-height', '32px', 'important');
      wrapper.style.setProperty('max-height', '32px', 'important');
    });
    
    // 设置 select wrapper 样式
    if (selectWrapper) {
      selectWrapper.style.setProperty('display', 'flex', 'important');
      selectWrapper.style.setProperty('align-items', 'center', 'important');
      selectWrapper.style.setProperty('padding-top', '0', 'important');
      selectWrapper.style.setProperty('padding-bottom', '0', 'important');
      selectWrapper.style.setProperty('height', '32px', 'important');
    }
    
    // 设置 el-input 元素样式
    inputElements.forEach(input => {
      input.style.setProperty('display', 'flex', 'important');
      input.style.setProperty('align-items', 'center', 'important');
      input.style.setProperty('height', '32px', 'important');
    });
    
    // 设置 inner 样式 - 使用更强制的方式
    inputInners.forEach(inner => {
      // 先移除所有可能影响对齐的样式
      inner.style.removeProperty('padding-top');
      inner.style.removeProperty('padding-bottom');
      inner.style.removeProperty('margin-top');
      inner.style.removeProperty('margin-bottom');
      inner.style.removeProperty('transform');
      
      inner.style.setProperty('height', '32px', 'important');
      inner.style.setProperty('min-height', '32px', 'important');
      inner.style.setProperty('max-height', '32px', 'important');
      inner.style.setProperty('line-height', '32px', 'important');
      inner.style.setProperty('display', 'flex', 'important');
      inner.style.setProperty('align-items', 'center', 'important');
      inner.style.setProperty('justify-content', 'flex-start', 'important');
      inner.style.setProperty('padding', '0', 'important');
      inner.style.setProperty('margin', '0', 'important');
      inner.style.setProperty('vertical-align', 'middle', 'important');
      
      // 一次性调整到位：文本居中显示（下拉框高度32px，向上移动9px使其居中）
      setTimeout(() => {
        const computedStyle = window.getComputedStyle(inner);
        const paddingTop = parseFloat(computedStyle.paddingTop);
        // 使用适中的负值向上移动文本，使其居中（32px高度，移动9px基本居中）
        const transformValue = paddingTop > 0 ? `translateY(-${paddingTop + 9}px)` : 'translateY(-9px)';
        
        inner.style.setProperty('transform', transformValue, 'important');
        inner.style.removeProperty('margin-top');
        
        // 验证transform是否被应用
        setTimeout(() => {
          const appliedTransform = window.getComputedStyle(inner).transform;
          if (appliedTransform === 'none' || !appliedTransform) {
            inner.style.setProperty('transform', 'translateY(-11px)', 'important');
          }
        }, 50);
      }, 10);
      
      // 直接设置所有子元素的样式
      const children = inner.children;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        child.style.removeProperty('padding-top');
        child.style.removeProperty('padding-bottom');
        child.style.removeProperty('margin-top');
        child.style.removeProperty('margin-bottom');
        child.style.removeProperty('transform');
        
        child.style.setProperty('line-height', '32px', 'important');
        child.style.setProperty('height', '32px', 'important');
        child.style.setProperty('min-height', '32px', 'important');
        child.style.setProperty('max-height', '32px', 'important');
        child.style.setProperty('display', 'inline-flex', 'important');
        child.style.setProperty('align-items', 'center', 'important');
        child.style.setProperty('justify-content', 'flex-start', 'important');
        child.style.setProperty('margin', '0', 'important');
        child.style.setProperty('padding', '0', 'important');
        child.style.setProperty('vertical-align', 'middle', 'important');
        
        // 如果子元素包含文本，也应用transform使其居中
        if (child.textContent && child.textContent.trim()) {
          child.style.setProperty('transform', 'translateY(-9px)', 'important');
          child.style.removeProperty('margin-top');
        }
        
        // 递归处理子元素
        const grandChildren = child.children;
        for (let j = 0; j < grandChildren.length; j++) {
          const grandChild = grandChildren[j];
          grandChild.style.removeProperty('padding-top');
          grandChild.style.removeProperty('padding-bottom');
          grandChild.style.removeProperty('margin-top');
          grandChild.style.removeProperty('margin-bottom');
          grandChild.style.removeProperty('transform');
          
          grandChild.style.setProperty('line-height', '32px', 'important');
          grandChild.style.setProperty('height', '32px', 'important');
          grandChild.style.setProperty('min-height', '32px', 'important');
          grandChild.style.setProperty('max-height', '32px', 'important');
          grandChild.style.setProperty('display', 'inline-flex', 'important');
          grandChild.style.setProperty('align-items', 'center', 'important');
          grandChild.style.setProperty('margin', '0', 'important');
          grandChild.style.setProperty('padding', '0', 'important');
          grandChild.style.setProperty('vertical-align', 'middle', 'important');
          
          // 如果子元素包含文本，也应用transform使其居中
          if (grandChild.textContent && grandChild.textContent.trim()) {
            grandChild.style.setProperty('transform', 'translateY(-9px)', 'important');
            grandChild.style.removeProperty('margin-top');
          }
        }
      }
    });
    
    // 设置选中项样式
    selectedItems.forEach(item => {
      item.style.removeProperty('padding-top');
      item.style.removeProperty('padding-bottom');
      item.style.removeProperty('margin-top');
      item.style.removeProperty('margin-bottom');
      
      item.style.setProperty('line-height', '32px', 'important');
      item.style.setProperty('height', '32px', 'important');
      item.style.setProperty('min-height', '32px', 'important');
      item.style.setProperty('max-height', '32px', 'important');
      item.style.setProperty('display', 'inline-flex', 'important');
      item.style.setProperty('align-items', 'center', 'important');
      item.style.setProperty('margin', '0', 'important');
      item.style.setProperty('padding', '0', 'important');
      item.style.setProperty('vertical-align', 'middle', 'important');
    });
    
    // 查找并设置所有可能的文本容器和文本节点
    const allTextContainers = selectContainer.querySelectorAll('span, div, p, label');
    allTextContainers.forEach(el => {
      // 只处理在输入框内的元素
      if (el.closest('.el-input__inner') || el.closest('.el-select__selected-item')) {
        el.style.removeProperty('padding-top');
        el.style.removeProperty('padding-bottom');
        el.style.removeProperty('margin-top');
        el.style.removeProperty('margin-bottom');
        el.style.removeProperty('transform');
        
        el.style.setProperty('line-height', '32px', 'important');
        el.style.setProperty('height', '32px', 'important');
        el.style.setProperty('min-height', '32px', 'important');
        el.style.setProperty('max-height', '32px', 'important');
        el.style.setProperty('display', 'inline-flex', 'important');
        el.style.setProperty('align-items', 'center', 'important');
        el.style.setProperty('margin', '0', 'important');
        el.style.setProperty('padding', '0', 'important');
        el.style.setProperty('vertical-align', 'middle', 'important');
        
        // 对所有包含文本的元素应用transform使其居中
        if (el.textContent && el.textContent.trim()) {
          el.style.setProperty('transform', 'translateY(-9px)', 'important');
          el.style.removeProperty('margin-top');
        }
      }
    });
    
    // 更激进的方法：直接查找所有包含文本的节点并应用transform
    setTimeout(() => {
      const allElements = selectContainer.querySelectorAll('*');
      allElements.forEach(el => {
        if (el.closest('.el-input__inner') || el.closest('.el-select__selected-item')) {
          const text = el.textContent || el.innerText;
          if (text && text.trim() && el.children.length === 0) {
            // 这是一个文本节点或只包含文本的叶子节点，应用transform使其居中
            el.style.setProperty('transform', 'translateY(-9px)', 'important');
            el.style.removeProperty('margin-top');
            el.style.setProperty('display', 'inline-block', 'important');
            el.style.setProperty('line-height', '32px', 'important');
          }
        }
      });
    }, 100);
  });
}

// 组件挂载后修复样式
onMounted(() => {
  // 延迟执行，确保 Element Plus 完全渲染
  setTimeout(() => {
    fixSelectTextAlignment();
  }, 100);
  
  // 使用 MutationObserver 监听 DOM 变化
  nextTick(() => {
    const selectContainer = document.querySelector('.select-meter-container');
    if (selectContainer) {
      const observer = new MutationObserver(() => {
        setTimeout(() => {
          fixSelectTextAlignment();
        }, 50);
      });
      
      observer.observe(selectContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }
  });
  
  // 再次延迟执行，确保所有样式都已应用
  setTimeout(() => {
    fixSelectTextAlignment();
  }, 300);
});

// 组件更新后也修复样式
onUpdated(() => {
  setTimeout(() => {
    fixSelectTextAlignment();
  }, 50);
});

</script>
<style lang="scss" scoped>
.page-container {
  --header-hight: 60px;
  & {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    // 卡片作为固定框架：占满外层滚动区高度，内部滚动，让筛选条件始终固定在顶部
    height: 100%;
    min-height: 100%;
    // 对齐 Figma：纯白卡片 + 圆角 + 阴影
   // background: #ffffff;
    border-radius: 24px;
   // box-shadow: 0 16px 40px rgba(15, 23, 42, 0.06);
   // border: 1px solid rgba(15, 23, 42, 0.03);
    padding: 16px 20px;
    box-sizing: border-box;
    // 本容器自身不滚动（隐藏溢出），滚动交给内部 .main-container，从而使 .header-container 固定不动
    overflow: hidden;

    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .header-container {
    & {
      width: 100%;
      min-height: var(--header-hight);
      height: auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      overflow: visible;
      // 固定顶部：作为 .page-container 弹性列的第一项，不参与内部滚动，始终可见
      position: relative;
      z-index: 30;
      background: #ffffff;
      padding: 0 20px 8px;
      flex-shrink: 0;
    }

    .left-container {
      flex: 1 1 560px;
      min-width: 0;
      max-width: 100%;
      min-height: var(--header-hight);
      padding: 8px 20px;
      display: flex;
      align-items: center;
      gap: 16px;
      position: relative;
      z-index: 10;
      flex-wrap: wrap;

      .time-picker-container {
        color: rgba(46, 46, 46, 0.50);
        font-family: "HarmonyOS Sans SC";
        font-size: 14px;
        font-style: normal;
        font-weight: 300;
        line-height: normal;
        stroke-width: 1px;
        stroke: #E0E4E7;
        width: min(294px, 100%);
        min-width: 220px;
        display: flex;
        align-items: center;
        height: 32px;
        max-height: 32px;
        overflow: hidden;
      }

      .select-meter-container {
        display: flex;
        align-items: center;
        gap: 6px;
        position: relative;
        z-index: 10;
        width: min(268px, 100%);
        min-width: 220px;
        height: 32px;
        max-height: 32px;
        overflow: hidden;

        .text-label {
          font-size: 16px;
          white-space: nowrap;
          color: rgba(46, 46, 46, 1);
          font-family: 'HarmonyOS Sans SC', sans-serif;
          line-height: 32px;
          flex-shrink: 0;
          height: 32px;
        }
      }
    }

    .right-container {
      min-width: 10%;
      height: 100%;
      padding: 0px 20px;
      display: flex;
      align-items: center;
      gap: 20px;

      .switch-container {
        display: flex;
        align-items: center;
        gap: 6px;

        .text-label {
          font-size: 16px;
          color: rgba(46, 46, 46, 1);
          font-family: 'HarmonyOS Sans SC', sans-serif;
        }
      }

    }




  }

  .main-container {
    & {
      width: 100%;
      min-height: 0;
      flex: 1;
      // 内部滚动区：图表 + 列表在此滚动，顶部筛选条件 .header-container 保持固定
      overflow-y: auto;
      overflow-x: hidden;
      padding: 10px 6px 6px;
      display: flex;
      flex-direction: column;
      gap: 0;
      min-width: 0;
    }

    .overview-grid {
      width: 100%;
      min-width: 0;
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(240px, 285px);
      grid-template-rows: 332px minmax(380px, auto);
      gap: 12px;
      flex-shrink: 0;
    }

    .overview-block {
      width: 100%;
      background: #ffffff;
      border-radius: 20px;
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
      border: 1px solid rgba(15, 23, 42, 0.02);
      padding: 12px 14px;
      overflow: hidden;

      display: flex;
      flex-direction: column;
    }

    .block-top-left {
      min-height: 332px;
    }

    .block-top-right {
      min-height: 328px;
    }

    .block-bottom-left {
      min-height: 380px;
      // 允许充放电统计图的 tooltip 溢出显示，避免被上方区域遮挡
      overflow: visible;
    }

    .block-bottom-right {
      min-height: 260px;
    }

    /* 现货口径下右上、右下均不渲染，网格合并为单列，左侧图表向右铺满整行 */
    .overview-grid.is-spot {
      grid-template-columns: 1fr;
    }

    .bottom-table-wrapper {
      width: 100%;
      margin-top: 12px;
      display: flex;
      justify-content: flex-start;
      flex-shrink: 0;
    }

    .bottom-table-block {
      /* 表格与背景撑满整个页面宽度 */
      width: 100%;
      min-width: 0;
      background: #ffffff;
      border-radius: 20px;
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
      border: 1px solid rgba(15, 23, 42, 0.02);
      padding: 12px 14px;
      box-sizing: border-box;
    }
  }

}

@media (max-width: 1200px) {
  .page-container {
    padding: 12px;

    .header-container {
      align-items: flex-start;
      flex-wrap: wrap;
    }

    .main-container {
      padding: 6px 0;

      .overview-grid {
        grid-template-columns: 1fr;
        grid-template-rows: none;
      }

      /* 单列布局下 grid 已合并，现货口径无需再做跨行定位 */
      .overview-grid.is-spot {
        grid-template-columns: 1fr;
      }
    }
  }
}

@media (max-width: 768px) {
  .page-container {
    padding: 10px;

    .header-container .left-container {
      padding: 6px 0;
    }
  }
}

// 确保Element Plus组件正常显示和交互
:deep(.el-radio-group) {
  display: inline-flex !important;
  flex-shrink: 0;
}

:deep(.el-radio-button) {
  pointer-events: auto !important;
}

:deep(.el-radio-button__inner) {
  cursor: pointer !important;
  pointer-events: auto !important;
  font-family: "HarmonyOS Sans SC" !important;
  font-size: 14px !important;
}

// 选中状态的按钮样式
:deep(.el-radio-button.is-active .el-radio-button__inner) {
  background-color: #3A68FF !important;
  border-color: #3A68FF !important;
  color: #ffffff !important;
  font-family: "HarmonyOS Sans SC" !important;
  font-size: 14px !important;
}

// 统一时间选择器和下拉框的高度
:deep(.el-date-editor) {
  width: 100% !important;
  min-width: 240px;
  height: 32px !important;
  max-height: 32px !important;
  min-height: 32px !important;
  font-size: 14px !important;
  overflow: hidden !important;
  border-radius: 999px !important;
  
  // 范围选择器的外层容器
  &.el-range-editor {
    border-radius: 999px !important;
  }
  
  > * {
    height: 32px !important;
    max-height: 32px !important;
  }
  
  .el-input {
    height: 32px !important;
    max-height: 32px !important;
    min-height: 32px !important;
    font-size: 14px !important;
    overflow: hidden !important;
  }
  
  .el-input__wrapper {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-left: 11px !important;
    padding-right: 30px !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    border-radius: 999px !important;
  }
  
  .el-input__inner {
    cursor: pointer !important;
    pointer-events: auto !important;
    height: 32px !important;
    max-height: 32px !important;
    min-height: 32px !important;
    line-height: 32px !important;
    padding: 0 !important;
    font-size: 14px !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    border-radius: 999px !important;
  }
  
  .el-input__prefix {
    height: 32px !important;
    max-height: 32px !important;
    line-height: 32px !important;
  }
  
  // 范围选择器的分隔符
  .el-range-separator {
    border-radius: 0 !important;
  }
}

:deep(.el-select) {
  width: 100% !important;
  min-width: 180px;
  height: 32px !important;
  max-height: 32px !important;
  border-radius: 999px !important;
  
  // Element Plus 新版本可能使用 el-select__wrapper
  .el-select__wrapper {
    border-radius: 999px !important;
  }
  
  .el-input {
    height: 32px !important;
    max-height: 32px !important;
  }
  
  .el-input__wrapper {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    box-sizing: border-box !important;
    border-radius: 999px !important;
    display: flex !important;
    align-items: center !important;
  }
  
  .el-input__inner {
    cursor: pointer !important;
    pointer-events: auto !important;
    height: 32px !important;
    max-height: 32px !important;
    min-height: 32px !important;
    line-height: 32px !important;
    padding: 0 11px !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    border-radius: 999px !important;
    display: flex !important;
    align-items: center !important;
    vertical-align: middle !important;
  }
}

// 确保电表下拉框容器内的下拉框正确对齐和高度
.select-meter-container {
  :deep(.el-select) {
    flex: 1;
    align-self: center;
    height: 32px !important;
    max-height: 32px !important;
    min-height: 32px !important;
    font-size: 14px !important;
    overflow: hidden !important;
    display: flex !important;
    align-items: center !important;
    
    > * {
      height: 32px !important;
      max-height: 32px !important;
    }
    
    .el-input {
      height: 32px !important;
      max-height: 32px !important;
      min-height: 32px !important;
      font-size: 14px !important;
      overflow: hidden !important;
      display: flex !important;
      align-items: center !important;
    }
    
    // Element Plus 新版本可能使用 el-select__wrapper
    .el-select__wrapper {
      border-radius: 999px !important;
      display: flex !important;
      align-items: center !important;
    }
    
    .el-input__wrapper {
      height: 32px !important;
      min-height: 32px !important;
      max-height: 32px !important;
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      padding-left: 11px !important;
      padding-right: 30px !important;
      box-sizing: border-box !important;
      overflow: hidden !important;
      border-radius: 999px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
    }
    
    .el-input__inner {
      height: 32px !important;
      max-height: 32px !important;
      min-height: 32px !important;
      line-height: 32px !important;
      padding: 0 !important;
      margin: 0 !important;
      font-size: 14px !important;
      box-sizing: border-box !important;
      overflow: hidden !important;
      border-radius: 999px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
    }
    
    // 确保输入框内的文本容器也居中
    .el-input__inner > * {
      line-height: 32px !important;
      height: 32px !important;
      display: inline-flex !important;
      align-items: center !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    // 针对 el-select 的特殊处理，确保文本垂直居中
    .el-select__selected-item {
      line-height: 32px !important;
      height: 32px !important;
      display: inline-flex !important;
      align-items: center !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    // 确保所有文本节点都垂直居中
    .el-input__inner span,
    .el-input__inner div,
    .el-select__selected-item span,
    .el-select__selected-item div {
      line-height: 32px !important;
      display: inline-flex !important;
      align-items: center !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    .el-input__suffix {
      height: 32px !important;
      max-height: 32px !important;
      line-height: 32px !important;
      display: flex !important;
      align-items: center !important;
    }
  }
}

// 确保下拉菜单可以正常显示
:deep(.el-select-dropdown),
:deep(.el-picker__popper) {
  z-index: 3000 !important;
  pointer-events: auto !important;
}

// 确保下拉框选项内容垂直居中
:deep(.el-select-dropdown__item) {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  height: auto !important;
  min-height: 34px !important;
  line-height: normal !important;
  padding: 8px 20px !important;
  
  .el-select-dropdown__item__content {
    display: flex !important;
    align-items: center !important;
    height: 100% !important;
    line-height: normal !important;
  }
}

// 确保header区域在loading时仍然可以交互
.header-container {
  position: relative;
  z-index: 100 !important;
  
  * {
    position: relative;
    z-index: 101 !important;
  }
}

// 确保loading遮罩不会覆盖header区域
:deep(.el-loading-mask) {
  z-index: 1 !important;
}

</style>
